import _, { isArray } from 'lodash';
import BaseEntity from 'src/entities/BaseEntity';
import { createEntities, createEntity } from 'src/entities/factory';
import { entityCollectionDefaultState, EntityCollectionState } from 'src/store/entities/types/EntityCollectionState';
import EntityDescription, { Projection } from 'src/store/entities/types/EntityDescription';
import { singleEntityDefaultState, SingleEntityState } from 'src/store/entities/types/SingleEntityState';

/*------------------------------------*\
 * Single Entity Selectors
\*------------------------------------*/

/**
 * Selects a single entity by id from the redux store state
 * and creates new instance by the given entity description
 */
export const getEntityById = (
    state: any,
    entityDescription: EntityDescription,
    id: string,
    projection: Projection = Projection.NONE,
    idField: string = 'id'
): SingleEntityState<any> => {
    const collection = getEntitiesByKeyValue(state, entityDescription, idField, id, projection);

    return {
        isFetching: collection.isFetching,
        error: collection.error,
        content: isArray(collection.items) && collection.items[0] ? collection.items[0] : undefined,
    };
};

/**
 * Selects a single entity by the given field name and value
 */
export const getEntityByFieldName = (
    state: any,
    entityDescription: EntityDescription,
    fieldName: string,
    value: string,
    projection: Projection = Projection.FULL
): SingleEntityState<any> => {
    const entitiesRoot = state?.entities;
    if (_.isEmpty(entitiesRoot) || _.isEmpty(entitiesRoot[entityDescription.reducer]))
        return singleEntityDefaultState();

    const entities = entitiesRoot[entityDescription.reducer][projection];

    if (_.isEmpty(entities)) return singleEntityDefaultState();

    // find raw json entity by id in store
    const jsonEntity = _.find(entities.items, entity => entity[fieldName] && entity[fieldName] === value);

    // create a new instance of the entity by the given entity description if available
    const content = jsonEntity ? createEntity(jsonEntity, entityDescription, projection) : undefined;

    return {
        isFetching: entities.isFetching,
        error: entities.error,
        content,
    };
};

/*------------------------------------*\
 * Entity Collection Selectors
\*------------------------------------*/

/**
 * Selects all information of the desired entity from the redux store state
 */
export const getEntities = (
    state: any,
    entityDescription: EntityDescription,
    projection: Projection = Projection.NONE,
    filter?: (entity: any) => boolean
): EntityCollectionState<any> => {
    const entitiesRoot = state?.entities;
    if (_.isEmpty(entitiesRoot) || _.isEmpty(entitiesRoot[entityDescription.reducer]))
        return entityCollectionDefaultState();

    const entitiesState = entitiesRoot[entityDescription.reducer][projection];

    if (entitiesState) {
        const items = createEntities(entitiesState.items, entityDescription, projection);
        return {
            isFetching: entitiesState.isFetching,
            error: entitiesState.error,
            items: filter ? items.filter(filter) : items,
        };
    }
    return entityCollectionDefaultState();
};

export function getFirstEntity<ENTITY extends BaseEntity = BaseEntity>(
    state: any,
    entityDescription: EntityDescription,
    projection: Projection = Projection.NONE,
    filter?: (entity: ENTITY) => boolean
): SingleEntityState<ENTITY | undefined> {
    const entitiesState = state?.entities?.[entityDescription.reducer]?.[projection];

    if (entitiesState) {
        const items = createEntities<ENTITY>(entitiesState.items, entityDescription, projection);

        return {
            isFetching: entitiesState.isFetching,
            error: entitiesState.error,
            content: filter ? items.find(filter) : items[0],
        };
    }

    return singleEntityDefaultState<ENTITY>();
}

/**
 * Selects all entities where the given id matches the idField for a specific EntityDescription.
 * For example:
 *      entityDescription = EntityDescriptions.SUPPLY
 *      id = 1
 *      idField = 'product'
 *
 * This call would select all supplies where the product id = 1
 */
export const getEntitiesByKeyValue = (
    state: any,
    entityDescription: EntityDescription,
    key: string,
    value: string,
    projection: Projection = Projection.NONE
): EntityCollectionState<any> => {
    const entitiesRoot = state?.entities;
    if (_.isEmpty(entitiesRoot) || _.isEmpty(entitiesRoot[entityDescription.reducer]))
        return entityCollectionDefaultState();

    const entities = entitiesRoot[entityDescription.reducer][projection];

    if (_.isEmpty(entities)) return entityCollectionDefaultState();

    return {
        isFetching: entities.isFetching,
        error: entities.error,
        items: createEntities(
            _.filter(entities.items, entity => entity[key] && entity[key] === value),
            entityDescription,
            projection
        ),
    };
};
